import { FilterStrategyType } from "../utils/user_picker";

import { UserType } from "./user";

export enum AvServiceType {
  Daily = "daily",
  Fake = "fake",
  Licode = "licode",
  Opentok = "opentok",
}

export interface BrandingType {
  page_title: string;
  logo_filename: string;
  logo_url: string;
  logo_sm_filename: string;
  logo_sm_url: string;
  favicon_filename: string;
  favicon_url: string;
}

export interface BreakoutType {
  id: string;
  grouping: string;
  title: string;
  breakout_rooms?: BreakoutRoomType[];
  duration_in_seconds: number | null;
  has_flavors: boolean;
  share_resource_between_groups: boolean;
}

export interface BreakoutRoomType {
  id: string;
  assigned_user_ids: string[];
  breakout_id: string;
  fixed_user_ids: string[];
  flavor: string;
  listening_user_ids: string[];
  panes: PaneType[];
  is_pinned: boolean;
  resource_ids: string[];
  room_id: string; // only used when creating class events
  talk_times?: { [userId: string]: number };
  title: string;
  visiting_user_ids: string[];
}

export interface BreakoutSessionType {
  id: string;
  breakout_id: string;
  // `started_on` and `ended_on` store ISO string representations of the date.
  started_on: string;
  ended_on: string | null;
}

export enum CapabilityType {
  CAN_ACCESS_MONITORING_DASHBOARD = "canAccessMonitoringDashboard",
  CAN_ALWAYS_RESET_CLASS = "canAlwaysResetClass",
  CAN_BECOME_TEACHER = "canBecomeTeacher",
  CAN_CHANGE_BANDWIDTH_MODE = "canChangeBandwidthMode",
  CAN_CONTROL_CLASSROOM = "canControlClassroom",
  CAN_EDIT_CLASS = "canEditClass",
  CAN_ENTER_CLASSROOM = "canEnterClassroom",
  CAN_MENTION_EVERYONE_IN_CHAT = "canMentionEveryoneInChat",
  CAN_PARTICIPATE_IN_CLASS = "canParticipateInClass",
  CAN_REFRESH_AV = "canRefreshAv",
  CAN_REPUBLISH_STREAMS = "canRepublishStreams",
  CAN_RESET_CLASS = "canResetClass",
  CAN_SEE_POLL_RESPONSES_AS_TEACHER = "canSeePollResponsesAsTeacher",
  CAN_START_RECORDING = "canStartRecording",
  CAN_VISIT_BREAKOUTS = "canVisitBreakouts",
  IS_ALWAYS_SUBSCRIBED_TO = "isAlwaysSubscribedTo",
  IS_VISIBLE_IN_CLASS = "isVisibleInClass",
}

export interface ChatType {
  id: string;
  created_on: number;
  user_id: string;
  text: string;
  likes: string[];
}

export interface ClassInfoType {
  id: string | undefined;
  title: string | undefined;
}

export type ClassroomInfoType = {
  id: string;
  allow_student_no_video: boolean;
  enable_student_audio_only: boolean;
  branding?: BrandingType;
  course: CourseType;
  date: number | null;
  disable_slideshow_recording: boolean;
  end_time: number | null;
  flight_server: string | null;
  flight_monitoring_server: string | null;
  is_in_flight: boolean;
  is_open: boolean;
  org: OrgType;
  room_id: string; // only used when creating class events
  section: SectionType;
  should_record_session: boolean;
  title: string;
  type: ClassTypeType;
};

export type ClassroomSliceStateType = {
  active_breakout_session_id: string | undefined;
  active_timeline_segment_id: string | undefined;
  av_service: AvServiceType | undefined;
  breakouts: { [id: string]: BreakoutType };
  breakout_rooms: { [id: string]: BreakoutRoomType };
  breakout_sessions: { [id: string]: BreakoutSessionType };
  capabilities: { [capability in CapabilityType]: string[] };
  chats: { [id: string]: ChatType };
  current_filter?: string;
  enable_student_drawing: boolean;
  info: ClassroomInfoType;
  latest_caption: LatestCaptionType;
  licode_server: string | undefined;
  max_subscribed_streams: number;
  opentok_session_id: string | undefined;
  participant_bar_user_ids: string[];
  polls: { [id: string]: PollType };
  poll_responses: { [id: string]: PollResponseType };
  poll_sessions: { [id: string]: PollSessionType };
  preclass_groups: { [id: string]: PreclassGroupType };
  preclass_resource_id: string | undefined;
  presenting_user_ids: string[];
  ptt_mode: boolean | false;
  recording_session_id: string | undefined;
  recording_state: RecordingStateType;
  resources: { [id: string]: ResourceType };
  stage: { panes: PaneType[]; featured_in_modal?: string };
  timeline: { id: string | undefined; sections: TimelineSectionType[]; is_empty?: boolean };
  timer: {
    durationMs: number;
    endsOn: string | null;
    timeRemainingWhenPausedMs: number | null;
    active: boolean;
    open: boolean;
  };
  users: { [id: string]: UserType };
  version: number | null;
};

export enum ClassTypeType {
  Virtual = "virtual",
  InPerson = "in-person",
  PcwOnly = "pcw-only",
  Asynchronous = "asynchronous",
}

interface CourseType {
  id: string | undefined;
  course_code: string;
}

export interface LatestCaptionType {
  created_on: number;
  text: string;
  room_id: string;
}

interface OrgType {
  id: number | undefined;
  name: string;
  short_name: string;
}

export enum PaneContentType {
  Empty = "empty",
  PollSession = "poll-session",
  Resource = "resource",
  User = "user",
}

export interface PaneType {
  content_id: string | undefined;
  content_type: PaneContentType;
  featured_poll_response?: FeaturedPollResponseType;
  hidden_in_presentation_view?: boolean;
  is_pinned?: boolean;
}

export interface PollChoiceType {
  id: string;
  choice: string;
  index: number;
}

export enum PollGradingTypeType {
  None = "none",
  Outcome = "outcome",
}

export interface PollType {
  id: string;
  question: string;
  title: string;
  type: PollTypeType;
  student_visible: boolean;
  multiple_response: boolean;
  poll_choices: PollChoiceType[];
  grading_type: PollGradingTypeType;
}

export interface PollResponseType {
  id: string;
  poll_session_id: string;
  user_id: string;
  response: string | number[];
  is_draft: boolean;
  submitted_at: number | null;
}

export interface FeaturedPollResponseType {
  id: string;
  response: string;
  user_id: string;
}

export enum PollSessionStatusType {
  Completed = "completed",
  InProgress = "in-progress",
}

export interface PollSessionType {
  id: string;
  poll_id: string;
  started_at: number;
  ended_at: number | null;
  status: PollSessionStatusType;
  view_results_as: PollSessionViewResultsAsType;
}

export enum PollTypeType {
  LongForm = "long-form",
  MultipleChoice = "mc",
  YesNo = "yn",
}

export enum PollSessionViewResultsAsType {
  Bar = "bar",
  Choices = "choices",
  Pie = "pie",
}

export interface PreclassGroupType {
  id: string;
  title: string;
  user_ids: string[];
}

export enum RecordingStateType {
  Preparing = "PREP",
  Starting = "START",
  Recording = "REC",
  NotRecording = "NOREC",
}

export interface ResourceType {
  id: string;
  title: string;
  type: ResourceTypeType;
  external_id: string;
  google_mimetype: string;
  html_slide_content: string[];
  is_breakout_group_notes: boolean;
  mux_details: MuxDetailsType | undefined;
  owner_id: string;
  page: number;
  preclass: boolean;
  precreated: boolean;
  subtype: MinervaResourceSubtypeType | undefined;
  thumbnail_url: string;
  uploader_id: string;
  url: string;
  video_state: VideoStateType | undefined;
  video_time_in_seconds: number | undefined;
  presentation_view: {
    [userId: string]: { scroll_x: number | undefined; scroll_y: number | undefined };
  };
  reset_whiteboard?: boolean;
}

export enum MinervaResourceSubtypeType {
  CaptionsDoc = "minerva-captions-doc",
  ForumCodeDoc = "minerva-notebook",
  ForumCodeWorkbook = "code-sled",
  ForumDoc = "minerva-doc",
  ForumWorkbook = "sled",
}

export interface MuxDetailsType {
  id: string;
  asset_id: string;
  playback_id: string;
  ready: boolean;
  error: string;
}

export enum ResourceTypeType {
  File = "file",
  GoogleDrive = "google-drive",
  HtmlSlide = "html-slide",
  ForumDoc = "minerva-doc",
  ForumWorkbook = "sle",
  Pdf = "pdf",
  Screenshare = "screenshare",
  Url = "url",
  Video = "video",
  Whiteboard = "whiteboard",
}

export enum VideoStateType {
  Paused,
  Playing,
}

interface SectionType {
  id: string | undefined;
  is_default: boolean;
  flight_group: string;
}

export enum TimelinePaneTypeType {
  Poll = "poll",
  PollResult = "poll-result",
  QuietStudent = "quiet-student",
  RandomStudent = "random-student",
  Resource = "resource",
  Teacher = "teacher",
}

export interface TimelinePaneType {
  name?: string;
  type: TimelinePaneTypeType;
  instance_back?: number;
}

export interface TimelineSectionType {
  id: string;
  duration_in_seconds: number;
  segments: TimelineSegmentType[];
  title: string;
}

export enum TimelineSegmentOpType {
  Breakout = "breakout",
  FeatureAllGroupsPeopleAndNotes = "feature-all-groups-people-and-notes",
  FeatureAllPreclassGroups = "feature-all-preclass-groups",
  Layout = "layout",
}

export interface FocusQuestionType {
  question: string;
  answer: string;
}

export interface InstructorNotesType {
  faculty_instructions?: string;
  engagement_prompt?: string;
  focus_questions?: FocusQuestionType[];
}

export interface TimelineSegmentType {
  id: string;
  op: TimelineSegmentOpType;
  panes: TimelinePaneType[] | null;
  title: string;
  breakout: string;
  notes?: InstructorNotesType;
  filter?:
    | FilterStrategyType.LowestFilterCount
    | FilterStrategyType.VotingYes
    | FilterStrategyType.VotingNo;
}
